import { defineStore } from 'pinia';
import { useIndicatorStore } from '~/store/indicator-item';
import { useModalStore } from '~/store/modal';
import type { ComposerTranslation } from 'vue-i18n';

export const useCommonStore = defineStore('common', {
  state: () => {
    return {
      //  多言語用
      t: {} as ComposerTranslation,
    };
  },
  getters: {},
  actions: {
    /**
     * 多言語対応用初期化処理
     */
    initialize(t: ComposerTranslation) {
      this.t = t;
    },

    /**
     * 回復可能なAPIエラーを共有アラートで表示する
     * 画面遷移せず、ユーザーが操作を続行できる
     *
     * @param message 表示メッセージ（省略時はデフォルトメッセージ）
     */
    async showRecoverableError(message?: string): Promise<void> {
      const modal = useModalStore();
      const indicator = useIndicatorStore();
      indicator.setIndicator(false);
      return new Promise<void>((resolve) => {
        modal.openConfirmModal(
          {
            title: this.t('message_sub.title_error'),
            message: message ?? this.t('message_sub.error_recoverable'),
            buttons: [
              {
                id: 'btn_ok',
                text: this.t('message_sub.btn_ok'),
                onClick: () => {
                  modal.closeConfirmModal();
                  resolve();
                },
              },
            ],
          },
          false
        );
      });
    },

    /**
     * 回復可能エラー用の fetchError コールバックを生成する
     * 401/403 は通常の fetchError（再ログイン）にフォールバックし、
     * それ以外はアラート表示で画面操作を継続可能にする
     *
     * @param message アラートに表示するメッセージ（省略時はデフォルト）
     */
    createRecoverableFetchError(message?: string): (statusCode: number) => Promise<void> {
      return async (statusCode: number) => {
        const { $STATUS_CODE } = useNuxtApp();
        if (
          statusCode === $STATUS_CODE.AUTHENTICATION_ERROR ||
          statusCode === $STATUS_CODE.FORBIDDEN
        ) {
          await this.fetchError(statusCode);
        } else {
          await this.showRecoverableError(message);
        }
      };
    },

    async fetchError(statusCode: number) {
      const { $STATUS_CODE, $ENV_NAME, $getEnvName, $CLOUD_COMMON_URL } = useNuxtApp();
      const indicator = useIndicatorStore();
      const tmpEnvName = $getEnvName();
      let envName = '';
      if (tmpEnvName !== $ENV_NAME.PROD) envName = tmpEnvName + '-';

      indicator.setIndicator(false);

      const router = useRouter();
      if (statusCode == $STATUS_CODE.AUTHENTICATION_ERROR || statusCode == $STATUS_CODE.FORBIDDEN) {
        if (statusCode == $STATUS_CODE.AUTHENTICATION_ERROR) {
          // 401エラーの場合確認メッセージを表示する。
          const modal = useModalStore();

          const promise = new Promise((resolve) => {
            modal.openConfirmModal({
              title: this.t('message_sub.title_error'),
              message: this.t('message_sub.error_1_1'),
              buttons: [
                {
                  id: 'btn_ok',
                  text: this.t('message_sub.btn_ok'),
                  onClick: async () => {
                    modal.closeAll();
                    resolve(true);
                  },
                },
              ],
            });
          });
          await promise;
        }
        // 共通基盤ログイン画面へ遷移
        window.location.href = $CLOUD_COMMON_URL.LOGIN.replace('{envName}', envName);
      } else {
        router.push({ path: `/error/${statusCode}` });
      }
    },
  },
});
